import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, inject} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {NgClass} from "@angular/common";
import {PasteCleanupSettingsModel, RichTextEditorAllModule, ToolbarSettingsModel} from "@syncfusion/ej2-angular-richtexteditor";
import {LanguageService} from "../../services/language.service";

@Component({
    selector: 'app-form-textarea',
    imports: [
        NgClass,
        ReactiveFormsModule,
        RichTextEditorAllModule
    ],
    templateUrl: './form-textarea.component.html',
    styleUrl: './form-textarea.component.scss',
})
export class FormTextareaComponent implements OnInit, OnChanges, OnDestroy {
  private languageService: LanguageService = inject(LanguageService);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('textAreaElement') public textAreaElement: any;

  @Input() public placeholder: string = '';
  @Input() public fCN: string = '';
  @Input() public class: string = '';
  @Input() public fG: FormGroup = new FormGroup({});
  @Input() public errorText: string = '';
  @Input() public style: 'inline' | 'block' | 'headless' | 'chat' = 'headless';
  @Input() public disabled: boolean = false;
  @Input() public showCharCounter: boolean = true;
  @Input() public rows: number = 3;
  @Input() public cols: number = 35;
  @Input() public maxChars: number = 0;
  @Input() public height: number = 200;
  @Input() public richText: boolean = false;
  @Input() public allowedToolbarItems: string[] = [];
  @Input() public setEmoji: string = '';
  @Output() public  blurred: EventEmitter<void> = new EventEmitter<void>();
  @Output() public  returnKeyPress: EventEmitter<void> = new EventEmitter<void>();
  @Output() public  documentsUpload: EventEmitter<void> = new EventEmitter<void>();
  @Output() public  setEmojiChange: EventEmitter<string> = new EventEmitter<string>();

  public pasteCleanupSettings: PasteCleanupSettingsModel = {
    prompt: false,
    plainText: false,
    keepFormat: false,

    /*
    POSSIBLE TAGS: "p", "strong", "em", "span", "h5", "ol", "li", "ul", "br"
    POSSIBLE ATTRS: "style"
    POSSIBLE STYLE PROPS: "text-decoration", "text-align"
    */

    deniedTags: [
      "a", "abbr", "acronym", "address", "applet", "area", "article", "aside", "audio",
      "b", "base", "basefont", "bdi", "bdo", "big", "blockquote", "body", "button",
      "canvas", "caption", "center", "cite", "code", "col", "colgroup", "data", "datalist",
      "dd", "del", "details", "dfn", "dialog", "dir", "div", "dl", "dt", "embed",
      "fieldset", "figcaption", "figure", "font", "footer", "form", "frame", "frameset",
      "h1", "h2", "h3", "h4", "h6", "head", "header", "hgroup", "hr", "html", "i",
      "iframe", "img", "input", "ins", "kbd", "label", "legend", "link", "main",
      "map", "mark", "meta", "meter", "nav", "noframes", "noscript", "object", "optgroup",
      "option", "output", "param", "picture", "pre", "progress", "q", "rp", "rt",
      "ruby", "s", "samp", "script", "section", "select", "small", "source", "strike",
      "style", "sub", "summary", "sup", "svg", "table", "tbody", "td", "template",
      "textarea", "tfoot", "th", "thead", "time", "title", "tr", "track", "tt", "u",
      "var", "video", "wbr"
    ],
    deniedAttrs: [
      "accept", "accept-charset", "accesskey", "action", "align", "alt", "async", "autocapitalize",
      "autocomplete", "autofocus", "autoplay", "background", "bgcolor", "border", "buffered",
      "capture", "challenge", "charset", "checked", "cite", "class", "code", "codebase", "color",
      "cols", "colspan", "content", "contenteditable", "contextmenu", "controls", "coords",
      "crossorigin", "csp", "data", "datetime", "decoding", "default", "defer", "dir",
      "dirname", "disabled", "download", "draggable", "enctype", "enterkeyhint", "for", "form",
      "formaction", "formenctype", "formmethod", "formnovalidate", "formtarget", "headers",
      "height", "hidden", "high", "href", "hreflang", "http-equiv", "id", "importance", "integrity",
      "intrinsicsize", "inputmode", "ismap", "itemid", "itemprop", "itemref", "itemscope",
      "itemtype", "kind", "label", "lang", "loading", "list", "loop", "low", "max", "maxlength",
      "minlength", "media", "method", "min", "multiple", "muted", "name", "novalidate", "open",
      "optimum", "pattern", "ping", "placeholder", "playsinline", "poster", "preload", "readonly",
      "referrerpolicy", "rel", "required", "reversed", "role", "rows", "rowspan", "sandbox",
      "scope", "scoped", "selected", "shape", "size", "sizes", "slot", "span", "spellcheck",
      "src", "srcdoc", "srclang", "srcset", "start", "step", "summary", "tabindex",
      "target", "title", "translate", "type", "usemap", "value", "width", "wrap"
    ],
    allowedStyleProps: ['text-align']
  };
  public toolbarSettings: ToolbarSettingsModel = {} as ToolbarSettingsModel;

  public ngOnInit(): void {
    if (this.allowedToolbarItems.length > 0) {
      this.toolbarSettings.items = [];
      if (this.allowedToolbarItems.includes('EmojiPicker')) {
        this.toolbarSettings.items.push('EmojiPicker');
      }
      if (this.allowedToolbarItems.includes('Image')) {
        this.toolbarSettings.items.push({
          tooltipText: this.languageService.getTranslationByKey('RICHTEXTSELECTIMAGETOOLTIP'), undo: false, click: this.toolbarClicked.bind(this), template: '<button type="button" class="e-tbar-btn e-btn e-lib e-icon-btn" tabindex="-1">'
            + '<span class="mts-icon fs-4 text-primary">photo_camera</span></button>'
        });
      }
    } else {
      this.toolbarSettings.items = ['EmojiPicker', 'Bold', 'Italic', 'Underline', {
        tooltipText: this.languageService.getTranslationByKey('RICHTEXTTITLETOOLTIP'), undo: true, click: this.toolbarClicked.bind(this), template: '<button type="button" class="e-tbar-btn e-btn e-lib e-icon-btn" tabindex="-1">'
          + '<span class="e-btn-icon e-caption e-icons"></span></button>'
      }, '|', 'Alignments', 'OrderedList', 'UnorderedList', '|', 'ClearFormat'];
    }

    this.setDisabledState(this.disabled);

    if (this.maxChars && !this.richText) {
      this.fG.controls[this.fCN].valueChanges.subscribe((value: string) => {
        if ((value?.length ?? 0) > this.maxChars) {
          this.fG.controls[this.fCN].setValue(value.substring(0, this.maxChars));
        }
      });
    }

    this.fG.controls[this.fCN].valueChanges.subscribe(value => {
      if (!this.richText) {
        if(value === null) {
          if (this.textAreaElement?.nativeElement) {
            this.textAreaElement.nativeElement.style.height = 'auto';
          }
        } else {
          this.adjustHeight();
        }
      }
      if (value === '<p><br/></p>' || value === '<p><br></p>') {
        this.fG.controls[this.fCN].setValue('');
      }
      // Sanitize text-decoration from value for richText
      if (this.richText && typeof value === 'string' && value.includes('text-decoration')) {
        // Only allow text-decoration if value is "underline"
        let sanitized = value.replace(/style=(["'])([^"']*?)text-decoration\s*:\s*([^;"']*)[;"']?/gi, (match, quote, styles, decorationValue) => {
          if (decorationValue.trim() === 'underline') {
        // Keep text-decoration: underline
        return match;
          }
          // Remove text-decoration from style string
          const newStyles = styles.replace(/text-decoration\s*:\s*[^;"']*;?\s*/gi, '');
          return `style=${quote}${newStyles}${quote}`;
        });
        // Remove empty style attributes
        sanitized = sanitized.replace(/style=(["'])\s*\1/gi, '');
        if (sanitized !== value) {
          this.fG.controls[this.fCN].setValue(sanitized);
        }
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.setDisabledState(changes['disabled'].currentValue);
    }
    if (changes['setEmoji'] && changes['setEmoji'].currentValue && !this.richText) {
      if (!this.textAreaElement?.nativeElement) {
        return;
      }
      const startPos = this.textAreaElement.nativeElement.selectionStart;
      const endPos = this.textAreaElement.nativeElement.selectionEnd;
      const text = this.textAreaElement.nativeElement.value;
      this.textAreaElement.nativeElement.value = text.slice(0, startPos) + changes['setEmoji'].currentValue + text.slice(endPos);
      const newCursorPos = startPos + changes['setEmoji'].currentValue.length;
      this.textAreaElement.nativeElement.setSelectionRange(newCursorPos, newCursorPos);
      this.textAreaElement.nativeElement.focus();
    }
  }

  private setDisabledState(disabled: boolean): void {
    const control = this.fG.controls[this.fCN];
    if (!control) return;
    if (disabled) {
      control.disable();
    } else {
      control.enable();
    }
  }

  public onReturnKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) {
        return;
      }
      if (this.style === 'chat') {
        event.preventDefault();
      }
      this.returnKeyPress.emit();
    }
  }

  public onPaste(event: ClipboardEvent): void {
    if (!this.maxChars) {
      return;
    }

    const clipboardText = event.clipboardData?.getData('text') ?? '';
    if (!clipboardText) {
      return;
    }

    const currentValue = this.fG.controls[this.fCN]?.value ?? '';
    const currentLength = this.getCurrentTextLength(currentValue);
    const selectedLength = this.getSelectedTextLength();
    const remainingLength = this.maxChars - (currentLength - selectedLength);

    if (remainingLength <= 0) {
      event.preventDefault();
      return;
    }

    if (clipboardText.length <= remainingLength) {
      return;
    }

    event.preventDefault();
    const trimmedText = clipboardText.slice(0, remainingLength);

    if (this.richText && this.textAreaElement?.executeCommand) {
      this.textAreaElement.executeCommand('insertText', trimmedText);
      return;
    }

    if (!this.textAreaElement?.nativeElement) {
      return;
    }

    const nativeElement = this.textAreaElement.nativeElement as HTMLTextAreaElement;
    const startPos = nativeElement.selectionStart ?? nativeElement.value.length;
    const endPos = nativeElement.selectionEnd ?? nativeElement.value.length;
    const text = nativeElement.value;
    const updatedText = text.slice(0, startPos) + trimmedText + text.slice(endPos);

    nativeElement.value = updatedText;
    const newCursorPos = startPos + trimmedText.length;
    nativeElement.setSelectionRange(newCursorPos, newCursorPos);
    this.fG.controls[this.fCN].setValue(updatedText);
  }

  public toolbarClicked(): void {
    if (this.allowedToolbarItems.includes('Image')) {
      this.documentsUpload.emit();
    } else {
      this.textAreaElement!.focusIn();
      const selection = document.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      let parentNode = range.commonAncestorContainer as HTMLElement;
      if (parentNode.nodeType === Node.TEXT_NODE) {
        parentNode = parentNode.parentElement!;
      }

      // Check if the selection is already within an <h5>
      if (parentNode.closest('h5')) {
        // If inside <h5>, remove it (convert to paragraph)
        this.textAreaElement.executeCommand('formatBlock', 'P');
      } else {
        // Otherwise, apply <h5>
        this.textAreaElement.executeCommand('formatBlock', 'H5');
      }
    }
  }

  public adjustHeight(): void {
    if (this.richText || !this.textAreaElement?.nativeElement) {
      return;
    }
    this.textAreaElement.nativeElement.style.height = 'auto';
    this.textAreaElement.nativeElement.style.height = Math.min(this.textAreaElement.nativeElement.scrollHeight, 150) + 'px';
  }

  private getCurrentTextLength(value: string | null): number {
    if (!value) {
      return 0;
    }

    if (!this.richText) {
      return value.length;
    }

    const tempElement = document.createElement('div');
    tempElement.innerHTML = value;
    return (tempElement.textContent || '').length;
  }

  private getSelectedTextLength(): number {
    if (!this.richText && this.textAreaElement?.nativeElement) {
      const nativeElement = this.textAreaElement.nativeElement as HTMLTextAreaElement;
      return Math.max(0, (nativeElement.selectionEnd ?? 0) - (nativeElement.selectionStart ?? 0));
    }

    const selection = document.getSelection();
    return selection ? selection.toString().length : 0;
  }

  public ngOnDestroy(): void {
    if (this.richText && this.textAreaElement) {
      try {
        this.textAreaElement.destroy();
      } catch (error) {
        console.error(error);
        // Silently handle if already destroyed
      }
    }
  }
}
